import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaCheckCircle,
  FaArrowRight,
  FaLinkedin,
  FaEnvelope,
  FaMedal,
  FaGlobe,
  FaBook,
  FaMicrophone,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

export default function FounderPage() {
  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            Leadership
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Venkata Siva Praveen Pusapati
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg max-w-2xl mx-auto mb-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            Founder &amp; CEO, XKMINNE Technologies
          </motion.p>
          <motion.div
            className="flex justify-center gap-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          >
            <a
              href="mailto:founder@xkminne.com"
              className="inline-flex items-center gap-2 bg-white text-blue-800 font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-sky-50 transition-colors"
            >
              <FaEnvelope /> Email Directly
            </a>
            <a
              href="https://www.linkedin.com/in/pusapati"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-white/10 transition-colors"
            >
              <FaLinkedin /> LinkedIn Profile
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Bio ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-14 items-start">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          >
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-3">About the Founder</p>
            <h2 className="text-3xl font-bold text-blue-900 mb-5">
              A Practitioner Who Built the Systems He Now Transforms
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Praveen brings over 10 years of hands-on experience operating inside regulated manufacturing environments. Before founding XKMINNE, he led AI and SAP transformations at some of the most demanding Class III medical device and pharmaceutical organizations in the world.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              His work spans FDA-regulated AI deployments, SAP PP/DS and TM implementations, compliance system architecture, and digital twin design — giving him a unique ability to bridge technical depth with operational reality.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Praveen founded XKMINNE with a clear conviction: that the gap between cutting-edge AI capabilities and the regulated manufacturing world was too large, and too consequential, to leave unfilled. He built XKMINNE to close that gap.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-7 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-5">Core Expertise</h3>
              <ul className="space-y-3">
                {[
                  "10+ years in Class III medical device systems",
                  "SAP PP/DS, TM, QM, IBP — implementation & optimization",
                  "FDA-regulated AI and machine learning deployments",
                  "GxP compliance architecture and CSV validation",
                  "Digital twin design and OT/IT integration",
                  "Global project delivery across US, EU, and APAC",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <FaCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Highlights ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Recognition & Thought Leadership</p>
            <h2 className="text-3xl font-bold text-blue-900">Beyond the Boardroom</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaBook className="text-2xl text-sky-600" />,
                title: "Published Author",
                desc: "Peer-reviewed publications on SAP supply chain AI and regulated manufacturing intelligence.",
              },
              {
                icon: <FaMicrophone className="text-2xl text-violet-600" />,
                title: "Tech Leadership Speaker",
                desc: "Featured guest on industry podcasts covering AI in manufacturing and supply chain transformation.",
              },
              {
                icon: <FaMedal className="text-2xl text-amber-500" />,
                title: "Advanced Education",
                desc: "Graduate-level credentials in engineering, systems architecture, and compliance management.",
              },
              {
                icon: <FaGlobe className="text-2xl text-emerald-600" />,
                title: "Global Delivery",
                desc: "Executed high-stakes projects across North America, Europe, and the Asia-Pacific region.",
              },
            ].map((h, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
              >
                <div className="mb-4 p-2.5 inline-flex rounded-lg bg-gray-50">{h.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{h.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-blue-900 text-white text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">Connect With Praveen</h2>
          <p className="text-blue-200 mb-8">
            Whether you're a potential client, partner, or investor — Praveen welcomes direct conversations about the future of intelligent regulated operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Schedule a Consultation <FaArrowRight />
            </Link>
            <a
              href="mailto:founder@xkminne.com"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              <FaEnvelope /> Email Directly
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
